import { useCallback, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import { ApiService, CompanyService, GenerationJobsService, RetrieveDocumentGenerationJob } from '../../api';
import { useStore } from '../../common/store';
import { UseStoreValues } from '../../common/types/store';
import { EventType, useEventBus } from '../../context';
import { useAsyncProcessor } from '../../context/AsyncProcessorContext/AsyncProcessorContext';
import { Logger } from '../../utilities';
import CompaniesService from '../companies';

export enum POLL_TYPE {
  GENERATION_JOBS = 'generation_jobs'
}

/**
 * Hook that handles app-level async actions on load.
 * This includes initializing polling for pending generation jobs.
 */
export const useAppLoadAsyncOperations = (initOnLoad = true) => {
  const { addJob, removeJob } = useAsyncProcessor();
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo } = storeValue;
  const { publish } = useEventBus();

  
  const initializeGenerationJobs = useCallback(async () => {
    const companiesService = new CompaniesService();
    try {
      if (!companyInfo?.id) {
        return;
      }

      const companyIdStr = companyInfo.id!.toString();

      // TODO: update the API here to enable or searching...
      // Fetch FINANCIAL_MAPPING and CAP_TABLE jobs with PENDING or IN_PROGRESS status
      // We make multiple calls because the API doesn't support OR conditions for status
      const [
        pendingFinMapJobs,
        inProgressFinMapJobs,
        pendingCapTableJobs,
        inProgressCapTableJobs,
      ] = await Promise.all([
        companiesService.getDocumentGenerationJobs(
          companyIdStr,
          undefined,
          undefined,
          RetrieveDocumentGenerationJob.status.PENDING,
          RetrieveDocumentGenerationJob.processing_type.FINANCIAL_MAPPING
        ),
        companiesService.getDocumentGenerationJobs(
          companyIdStr,
          undefined,
          undefined,
          RetrieveDocumentGenerationJob.status.IN_PROGRESS,
          RetrieveDocumentGenerationJob.processing_type.FINANCIAL_MAPPING
        ),
        companiesService.getDocumentGenerationJobs(
          companyIdStr,
          undefined,
          undefined,
          RetrieveDocumentGenerationJob.status.PENDING,
          RetrieveDocumentGenerationJob.processing_type.CAP_TABLE
        ),
        companiesService.getDocumentGenerationJobs(
          companyIdStr,
          undefined,
          undefined,
          RetrieveDocumentGenerationJob.status.IN_PROGRESS,
          RetrieveDocumentGenerationJob.processing_type.CAP_TABLE
        ),
      ]) as [
        RetrieveDocumentGenerationJob[],
        RetrieveDocumentGenerationJob[],
        RetrieveDocumentGenerationJob[],
        RetrieveDocumentGenerationJob[],
      ];

      const activeFinMapJobs = [...pendingFinMapJobs, ...inProgressFinMapJobs];
      const activeCapTableJobs = [...pendingCapTableJobs, ...inProgressCapTableJobs];

      if (activeFinMapJobs.length > 0) {
        Logger.info('useAppLoadAsyncActions:', 'Found active FINANCIAL_MAPPING jobs to poll', activeFinMapJobs.length);
      }
      if (activeCapTableJobs.length > 0) {
        Logger.info('useAppLoadAsyncActions:', 'Found active CAP_TABLE jobs to poll', activeCapTableJobs.length);
      }

      // Add each active FINANCIAL_MAPPING job to polling
      activeFinMapJobs.forEach((job: RetrieveDocumentGenerationJob) => {
        addJob({
          id: job.job_id,
          details: {
            title: 'Financial Mapping',
            description: `Processing job ${job.job_id}`,
          },
          pollingConfig: {
            pollingFunction: async () => {
              const result = await CompanyService.apiCompanyDocumentGenerationJobsRead(
                companyIdStr,
                job.job_id
              );
              return result;
            },
            pollingInterval: 5000,
            pollingArgs: [],
          },
          onResult: async (result: RetrieveDocumentGenerationJob) => {
            if (result.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
              try {
                await GenerationJobsService.apiGenerationJobsAttributeMappingsCreate(result.job_id);
                publish(EventType.FIN_MAP_ATT_MAPPER_UPDATED_VIA_GEN_JOB_COMPLETED, result.job_id);
                enqueueSnackbar('Financial mapping completed and created', { variant: 'success' });
              } catch (error) {
                Logger.error('useAppLoadAsyncActions - Error creating mapping for completed job', error);
                enqueueSnackbar('Job completed but mapping creation failed', { variant: 'warning' });
              }
              removeJob(result.job_id);
            } else if (result.status === RetrieveDocumentGenerationJob.status.FAILED) {
              removeJob(result.job_id);
            }
          },
          onError: (error: Error) => {
            Logger.error('useAppLoadAsyncActions - Generation job polling error', error);
          },
        });
      });

      // Add each active CAP_TABLE job to polling
      activeCapTableJobs.forEach((job: RetrieveDocumentGenerationJob) => {
        addJob({
          id: job.job_id,
          details: {
            title: 'Generating Cap Table',
            description: `Processing job ${job.job_id}`,
          },
          pollingConfig: {
            pollingFunction: async () => {
              const result = await CompanyService.apiCompanyDocumentGenerationJobsRead(
                companyIdStr,
                job.job_id
              );
              return result;
            },
            pollingInterval: 3000,
            pollingArgs: [],
          },
          onResult: async (result: RetrieveDocumentGenerationJob) => {
            if (result.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
              try {
                const applyResult = (await ApiService.apiCompanyCapTablesApplyGenerationJobCreate(
                  companyIdStr,
                  { job_id: result.job_id }
                )) as unknown as { security_ids?: number[] };

                const count = applyResult?.security_ids?.length ?? 0;
                publish(EventType.CAP_TABLE_GENERATION_JOB_COMPLETED, result.job_id);
                enqueueSnackbar(
                  count > 0
                    ? `Cap table updated with ${count} securities from AI results`
                    : 'Cap table generation completed',
                  { variant: 'success' }
                );
              } catch (applyError) {
                Logger.error('useAppLoadAsyncActions - Error applying cap table generation job', applyError);
                enqueueSnackbar('Job completed but applying results to the cap table failed', {
                  variant: 'warning',
                });
              }
              removeJob(result.job_id);
            } else if (result.status === RetrieveDocumentGenerationJob.status.FAILED) {
              enqueueSnackbar('Cap table generation job failed', { variant: 'error' });
              removeJob(result.job_id);
            }
          },
          onError: (error: Error) => {
            Logger.error('useAppLoadAsyncActions - Cap table generation job polling error', error);
          },
        });
      });
    } catch (error) {
      Logger.error('useAppLoadAsyncActions - Error initializing jobs from API', error);
    }
    // companiesService is not used in the dependencies because it is a new instance on each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo, addJob, removeJob, publish]);

  const manuallyExecutePoll = (pollType: POLL_TYPE.GENERATION_JOBS) => {
    if (pollType === POLL_TYPE.GENERATION_JOBS) {
      initializeGenerationJobs()
    }
  }

  useEffect(() => {
    if (initOnLoad) {
      initializeGenerationJobs();
    }
  }, [initOnLoad, initializeGenerationJobs]);

  return {
    manuallyExecutePoll
  }
};

export default useAppLoadAsyncOperations;
